// NippouPage — 日報処理ページ (2026-06-15 自動生成システム拡張)
//
// スマレジ → freee 一括登録 の生成→確認→承認→登録 chain。
//   - 生成バー: 日付範囲 (date_from/date_to) + 「生成」 (generate dry_run:false / detect_held+blocked)
//   - KPI/statusフィルタ: proposed / held_kakeuri / held_unknown / blocked_no_bank / held_imbalance /
//     registered の件数バッジ (client 側集計) + 着色フィルタ
//   - 複式カード (借方左・貸方右) + status 別の held/blocked 対応 UI:
//       held_kakeuri  → 掛売 instance に勘定(売掛金/未収金/広告宣伝費)+取引先(or 取引先なし)+品目+メモタグ+
//                        備考(line_note=freee摘要)+方針を指示
//                        (kakeuri-directive: no_partner/item_id/add_tag_ids/line_note 対応・2026-07 拡張)
//                        プリセットチップ2種 (インフルエンサー飲食代/クレカ通信エラー) で全フィールド自動入力
//                        (適用は従来の[適用]ボタン=誤爆防止。品目はitemsMapの実在名解決のみ・でっち上げ禁止)
//       held_unknown  → unknown_signals を category 分類 (learn-classification) → 再生成(force)
//       blocked_no_bank → bank_match/expected_deposit_date/cash_total Note + 「再評価」(bank-reeval)
//       held_imbalance → 貸借不一致/突合不能の調査要 Note
//   - 承認 dry_run プレビュー: approve(dry_run:true) → 送信予定 deal payload + Σ検算 → 「freeeに登録 ▸」
//   - 一括承認・登録: bulk-approve(dry_run:true→false)
//   - 台帳パネル(Accordion): 学習 pending (/learning) + 入金待ち (/pending-bank)
//
// 注: dry_run:false の実 freee 登録は user の承認 click 起点のみ (R2/R6 [[feedback_verification_cleanup]])。
//     生成 / dry_run プレビュー / 再評価 / 学習 / 掛売指示 は freee 非 POST。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  PageTitle,
  Button,
  SelectBox,
  Note,
  StatusIcon,
  InlineSpinner,
  TaskDialog,
  TextArea,
  FloatingMessageBlock,
  MessageBlock,
  AccordionPanel,
  ProgressBar,
} from '@freee_jp/vibes';
import {
  MdRestaurantMenu,
  MdRefresh,
  MdCheckCircle,
  MdWarning,
  MdStore,
  MdEventNote,
  MdAutoAwesome,
  MdAccountBalance,
  MdSchool,
} from 'react-icons/md';
import {
  fetchNippouJournalsRange,
  approveNippouJournal,
  approveNippouJournalDryRun,
  rejectNippouJournal,
  generateNippouJournals,
  bulkApproveNippouDryRun,
  bulkApproveNippou,
  saveNippouKakeuriDirective,
  saveNippouClassification,
  fetchNippouLearning,
  fetchNippouBankPending,
  reevalNippouBank,
  fetchPartnersMap,
  fetchItemsMap,
  fetchTagsMap,
  NippouJournal,
  NippouDeal,
  NippouDealDetail,
  NippouApproveDryRun,
  NippouBulkDryRun,
  NippouBulkResult,
  NippouLearningRow,
  NippouBankPendingRow,
  NIPPOU_KAKEURI_ACCOUNTS,
  API_BASE,
  notifyAuditBlocked,
} from '../api/client';
import { nippouStatusToUnified, UNIFIED_LEGEND } from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// session (= JournalVerificationPage の ensureSessionLocal 流儀)
// ---------------------------------------------------------------------------
async function ensureSessionLocal(): Promise<string | null> {
  try {
    const existing = localStorage.getItem('claude_session_id');
    if (existing) {
      const hb = await fetch(`${API_BASE}/api/claude/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: existing }),
      }).catch(() => null);
      if (hb && hb.ok) return existing;
    }
    const r = await fetch(`${API_BASE}/api/claude/session/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actor: 'browser-ui', scope: 'nippou_verification', lease_minutes: 120 }),
    });
    if (!r.ok) {
      // 2026-07-15 blockfix: audit blocking 409 は共通ダイアログ (自動修復ボタン付き) へ通知
      if (r.status === 409) notifyAuditBlocked(await r.json().catch(() => null));
      return null;
    }
    const data = await r.json();
    if (data.session_id) {
      localStorage.setItem('claude_session_id', data.session_id);
      return data.session_id;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const yen = (n: number | null | undefined) =>
  n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`;

const DAY_LABEL = ['日', '月', '火', '水', '木', '金', '土'];
const dayOfWeek = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  return DAY_LABEL[d.getUTCDay()];
};

// 月初/月末 (YYYY-MM-DD)
function monthStart(ym: string): string { return `${ym}-01`; }
function monthEnd(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(last).padStart(2, '0')}`;
}

// 全店舗 section_id (worker NIPPOU_STORES と一致)。 範囲生成は section ごとに分割実行する
//   (遠隔店の銀行突合 index を範囲全体で構築するため + 月一括 1 リクエストの timeout 回避)。
const NIPPOU_ALL_SECTIONS = [3068544, 3068545, 3068546, 3068547, 3068548, 3068549, 3068550, 3068552, 3068553, 3068554];

// 現在の月 (YYYY-MM)。 日報を開いた直後はこの月を表示する
function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// 過去 15 ヶ月 + '2026-04' の month option (currentYearMonth と同じ local 時刻基準 → 初期月が必ず option に含まれる)
function buildMonthOptions(): string[] {
  const set = new Set<string>(['2026-04']);
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 15; i++) {
    set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return Array.from(set).sort().reverse();
}

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

// 一括登録フルスクリーン進捗 (2026-07-13 280件実戦監査 fix — 請求書一括承認 (JournalVerificationPage) の流儀:
//   グレーアウト(操作不可) + n/N カウンタ + 失敗の画面表示 + [再試行])
//   running = freee 登録実行中 / error = 失敗・中断あり (明細を画面に大きく表示し console に埋めない)
interface BulkRegisterProgress {
  phase: 'running' | 'error';
  storeLabel: string;   // 現在処理中の店舗 (i/N店舗 sec xxx)
  done: number;         // 処理済 journal (登録+失敗) 累計
  total: number;        // dry_run eligible_count (登録対象 journal 総数)
  registered: number;
  failed: number;
  failures: string[];   // 失敗明細 (画面表示は先頭 20 件)
  errorText?: string;
}

// status メタ — 語・色は lib/statusVocab.ts (nippouStatusToUnified) の統一語彙に完全委譲。
//   橙=あなたの対応待ち (承認/指示/修正) / 青=処理中 (blocked_no_bank は銀行入金の自動到着待ち=操作不要) /
//   緑=完了 / グレー=除外。停止理由 (貸借不一致等) はカード内の held/blocked 事実表示に残る。
const STATUS_META: Record<string, { label: string; color: string }> = Object.fromEntries(
  [
    'suggested', 'proposed', 'approved', 'rejected', 'registered', 'error',
    'held_kakeuri', 'held_unknown', 'blocked_no_bank', 'held_imbalance', 'held_bank_unmatched',
  ].map((s) => {
    const u = nippouStatusToUnified(s);
    return [s, { label: u.label, color: u.fg }];
  }),
);
const statusLabel = (s: string) => STATUS_META[s]?.label || nippouStatusToUnified(s).label;
const statusColor = (s: string) => STATUS_META[s]?.color || nippouStatusToUnified(s).fg;

// フィルタ可能 status (件数バッジ順)
const FILTER_STATUSES = [
  'proposed', 'held_kakeuri', 'held_unknown', 'blocked_no_bank', 'held_bank_unmatched', 'held_imbalance', 'registered',
  'suggested', 'approved', 'rejected', 'error',
];

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  controlBar: {
    display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' as const,
    padding: '12px 16px', background: '#fff',
    border: '1px solid #e5e4e2', borderRadius: '10px', marginBottom: '14px',
  } as React.CSSProperties,
  field: { display: 'flex', flexDirection: 'column' as const, gap: '4px' },
  dateInput: {
    padding: '6px 8px', border: '1px solid #d6d3d0', borderRadius: 6,
    fontSize: '0.8125rem', height: 32,
  } as React.CSSProperties,
  fieldLabel: {
    fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase' as const,
  } as React.CSSProperties,
  kpiRow: {
    display: 'flex', gap: '8px', flexWrap: 'wrap' as const, marginBottom: '14px',
  } as React.CSSProperties,
  kpiChip: (active: boolean, color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
    padding: '5px 12px', borderRadius: '8px', fontSize: '0.8125rem', fontWeight: 600,
    background: active ? color : `${color}18`, color: active ? '#fff' : color,
    border: `1px solid ${active ? color : `${color}44`}`, userSelect: 'none' as const,
  }),
  card: (status: string): React.CSSProperties => {
    const c = statusColor(status);
    return {
      background: '#fff', border: '1px solid #e5e4e2', borderLeft: `5px solid ${c}`,
      borderRadius: '10px', padding: '16px 20px', marginBottom: '14px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    };
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: '12px', marginBottom: '12px', flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  badge: (color: string): React.CSSProperties => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
    background: `${color}22`, color, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em',
  }),
  storeTitle: { fontWeight: 700, fontSize: '1rem' } as React.CSSProperties,
  dateSub: { fontSize: '0.8125rem', color: '#8a837c', marginTop: '2px' } as React.CSSProperties,
  table: {
    width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.8125rem', marginTop: '4px',
    tableLayout: 'fixed' as const,
  } as React.CSSProperties,
  th: {
    padding: '6px 10px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    textTransform: 'uppercase' as const, letterSpacing: '0.03em',
    borderBottom: '1px solid #e5e4e2', background: '#fafaf8', textAlign: 'left' as const,
  } as React.CSSProperties,
  thAmt: { textAlign: 'right' as const },
  td: {
    padding: '6px 10px', borderBottom: '1px solid #f4f3f1', verticalAlign: 'top' as const,
    wordBreak: 'break-word' as const,
  } as React.CSSProperties,
  amt: {
    textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const,
    whiteSpace: 'nowrap' as const, fontWeight: 500,
  } as React.CSSProperties,
  caption: { fontSize: '0.6875rem', color: '#8a837c', marginTop: '2px' } as React.CSSProperties,
  divider: { borderLeft: '1px solid #e5e4e2' } as React.CSSProperties,
  totalRow: { fontWeight: 700, background: '#fafaf8' } as React.CSSProperties,
  actions: { display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' as const } as React.CSSProperties,
  warningBox: {
    marginTop: '12px', padding: '8px 12px',
    background: '#fff4e2', border: '1px solid #f5d8a8', borderRadius: '6px',
    color: '#8b5a14', fontSize: '0.8125rem',
  } as React.CSSProperties,
  heldBox: {
    marginTop: '12px', padding: '12px 14px',
    background: '#fdf1f1', border: '1px solid #f0c4c4', borderRadius: '8px',
  } as React.CSSProperties,
  heldTitle: {
    display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#b23030',
    fontSize: '0.875rem', marginBottom: 8,
  } as React.CSSProperties,
  inlineRow: {
    display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' as const, marginTop: 8,
  } as React.CSSProperties,
  // 見やすい内訳サマリー
  sumGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: 4 } as React.CSSProperties,
  sumSection: { background: '#fafaf8', border: '1px solid #eeede9', borderRadius: 8, padding: '10px 12px' } as React.CSSProperties,
  sumLabel: { fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 6 } as React.CSSProperties,
  sumLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '3px 0' } as React.CSSProperties,
  sumChip: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 5, fontSize: '0.625rem', fontWeight: 700, color: '#fff', marginRight: 6, flexShrink: 0 } as React.CSSProperties,
  sumName: { fontSize: '0.84rem' } as React.CSSProperties,
  sumSub: { fontSize: '0.6875rem', color: '#8a837c' } as React.CSSProperties,
  sumAmt: { fontVariantNumeric: 'tabular-nums' as const, fontWeight: 600, whiteSpace: 'nowrap' as const } as React.CSSProperties,
  sumTotal: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e5e4e2', paddingTop: 6, marginTop: 6, fontWeight: 700, fontSize: '0.875rem' } as React.CSSProperties,
  toggleLink: { background: 'none', border: 'none', color: '#48859d', cursor: 'pointer', fontSize: '0.75rem', padding: '6px 0 0', textDecoration: 'underline' } as React.CSSProperties,
  matchNote: { marginTop: 8, fontSize: '0.75rem', color: '#48859d', background: '#eef5f8', borderRadius: 6, padding: '6px 10px' } as React.CSSProperties,
  misattrNote: { marginTop: 8, fontSize: '0.78rem', color: '#8b5a14', background: '#fff4e2', border: '1px solid #f3d39a', borderRadius: 6, padding: '6px 10px', fontWeight: 600 } as React.CSSProperties,
  // 複式仕訳ビュー (借方左/貸方右・freeeタグ付き)
  jPanel: { marginTop: 8, border: '1px solid #ece9e4', borderRadius: 10, overflow: 'hidden', background: '#fff' } as React.CSSProperties,
  jGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr' } as React.CSSProperties,
  jColHead: (side: 'debit' | 'credit'): React.CSSProperties => ({
    padding: '7px 14px', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.1em',
    color: side === 'debit' ? '#2e6fb0' : '#2a8a5c',
    background: side === 'debit' ? '#f4f8fc' : '#f2faf5',
    borderBottom: `2px solid ${side === 'debit' ? '#c4dbf0' : '#c2e7d2'}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  }),
  jColHint: { fontSize: '0.6rem', fontWeight: 600, color: '#b0a89e', letterSpacing: 0 } as React.CSSProperties,
  jEntry: { padding: '9px 14px', borderBottom: '1px solid #f4f3f1' } as React.CSSProperties,
  jEntryTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 } as React.CSSProperties,
  jAccChip: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 19, height: 19, borderRadius: 5, fontSize: '0.62rem', fontWeight: 700, color: '#fff', flexShrink: 0 } as React.CSSProperties,
  jAcc: { fontSize: '0.875rem', fontWeight: 600, color: '#2a2723', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as React.CSSProperties,
  jAmt: { fontSize: '0.9rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap', color: '#1f1d1a' } as React.CSSProperties,
  jTags: { display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginTop: 6, paddingLeft: 26 } as React.CSSProperties,
  jTag: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.68rem', color: '#6b6560', background: '#f4f3f1', borderRadius: 4, padding: '1px 6px' } as React.CSSProperties,
  jTagK: { fontSize: '0.58rem', fontWeight: 700, color: '#aaa093', letterSpacing: '0.02em' } as React.CSSProperties,
  jTaxTag: { fontSize: '0.68rem', color: '#3f6489', background: '#eaf1f8', borderRadius: 4, padding: '1px 6px', fontWeight: 600 } as React.CSSProperties,
  jVatTag: { fontSize: '0.66rem', color: '#9b938a', fontStyle: 'italic' as const, alignSelf: 'center' } as React.CSSProperties,
  jWarnTag: { fontSize: '0.68rem', color: '#b3742e', background: '#fff4e2', borderRadius: 4, padding: '1px 6px', fontWeight: 700 } as React.CSSProperties,
  jColTotal: { display: 'flex', justifyContent: 'space-between', padding: '8px 14px', fontWeight: 700, fontSize: '0.84rem', background: '#fafaf8', borderTop: '1px solid #eeede9', fontVariantNumeric: 'tabular-nums' as const } as React.CSSProperties,
  jBalance: (ok: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px',
    fontWeight: 700, fontSize: '0.85rem', color: ok ? '#2da663' : '#c94444',
    background: ok ? '#f1faf4' : '#fdf1f1', borderTop: '1px solid #ece9e4',
  }),
  jFooter: { padding: '6px 14px', fontSize: '0.7rem', color: '#a59c90', background: '#fafaf8', borderTop: '1px solid #f0efec', display: 'flex', gap: 12, flexWrap: 'wrap' as const } as React.CSSProperties,
  modalOverlay: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.42)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' as const,
  } as React.CSSProperties,
  modalCard: {
    background: '#fff', borderRadius: '10px', maxWidth: '880px', width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.24)', overflow: 'hidden',
  } as React.CSSProperties,
};

// flatten された複式行
interface FlatRow {
  side: 'debit' | 'credit';
  dealType?: 'income' | 'expense';
  detail?: NippouDealDetail;
  dealPartnerName?: string | null;
  dealPartnerId?: number | null;
  synthetic?: { name: string; amount: number };
}

// audit_json から表示すべき警告メッセージ群を抽出 (柔軟な shape に耐える)
function extractWarnings(audit: any): string[] {
  if (!audit) return [];
  const out: string[] = [];
  const pushVal = (v: any) => {
    if (v == null) return;
    if (typeof v === 'string') { if (v.trim()) out.push(v.trim()); return; }
    if (Array.isArray(v)) { v.forEach(pushVal); return; }
    if (typeof v === 'object') {
      const msg = v.message || v.msg || v.text || v.reason || v.detail;
      if (msg) out.push(String(msg));
    }
  };
  if (typeof audit === 'object' && !Array.isArray(audit)) {
    for (const key of ['warnings', 'warning', 'notes', 'alerts', 'issues']) {
      if (audit[key] != null) pushVal(audit[key]);
    }
    const labelMap: Record<string, string> = {
      new_payment_method: '新しい決済方法を検出',
      new_discount: '新しい値引を検出',
      smaregi_mismatch: 'スマレジ突合差異',
      gross_mismatch: 'グロス計算差異',
    };
    for (const [k, label] of Object.entries(labelMap)) {
      const v = audit[k];
      if (v && typeof v !== 'object') out.push(typeof v === 'string' ? `${label}: ${v}` : label);
    }
  }
  return Array.from(new Set(out));
}

// held 要因の取り出し (audit_json.held_factors を優先、 status から fallback)
function heldFactors(j: NippouJournal): string[] {
  const a: any = j.audit_json;
  if (a && Array.isArray(a.held_factors) && a.held_factors.length) return a.held_factors;
  if (j.status?.startsWith('held_') || j.status === 'blocked_no_bank') return [j.status];
  return [];
}

// 掛売 instance の取り出し (audit_json.kakeuri_instances → deals_json marker の順)
interface KakeuriInstance { line_ref: string | null; source_key?: string; amount?: number; }

// 掛売指示の入力値 (KakeuriDirectiveRow → onKakeuri → saveNippouKakeuriDirective)。
//   noPartner=true (取引先なし) 時は partnerId=null。 itemId(品目)/addTagIds(メモタグ) は任意。
//   lineNote = 備考 (freee 摘要に入る line_note)。 note = 方針メモ (監査用・freee には入らない)。
interface KakeuriDirectiveInput {
  accountId: number;
  partnerId: number | null;
  noPartner: boolean;
  itemId: number | null;
  addTagIds: number[];
  note: string;
  lineNote: string;
}
function kakeuriInstances(j: NippouJournal): KakeuriInstance[] {
  const a: any = j.audit_json;
  if (a && Array.isArray(a.kakeuri_instances) && a.kakeuri_instances.length) {
    return a.kakeuri_instances;
  }
  // deals_json の _needs_kakeuri_directive marker から導出
  const out: KakeuriInstance[] = [];
  const deals: NippouDeal[] = Array.isArray(j.deals_json) ? j.deals_json : [];
  deals.forEach((deal, di) => {
    (deal.details || []).forEach((d: any, ci) => {
      if (d && d._needs_kakeuri_directive === true) {
        out.push({ line_ref: `${di}:${ci}`, source_key: d._source_key, amount: Number(d.amount) || 0 });
      }
    });
  });
  return out;
}

// 未知 signal (audit_json.unknown_signals)
interface UnknownSignal { kind: string; contract_id?: string | null; source_key: string; source_name?: string | null; }
function unknownSignals(j: NippouJournal): UnknownSignal[] {
  const a: any = j.audit_json;
  if (a && Array.isArray(a.unknown_signals) && a.unknown_signals.length) return a.unknown_signals;
  return [];
}

// ---------------------------------------------------------------------------
// 複式簿記カード
// ---------------------------------------------------------------------------
// 勘定科目ID → 見やすい内訳表示のアイコン/色 (借方=回収・充当の種別)
const NACC = { sales: 858355802, mishuu: 891995955, nyukinki: 866372036, souiki: 864550386, shain: 864550582, koukoku: 858355826 };
function nippouLineMeta(accId: number | undefined | null, side: 'debit' | 'credit'): { icon: string; tone: string } {
  if (side === 'credit') return { icon: '売', tone: '#2da663' };
  switch (Number(accId)) {
    case NACC.nyukinki: return { icon: '現', tone: '#2e7cf6' };  // 入金機投入高(現金)
    case NACC.mishuu:   return { icon: 'カ', tone: '#7a5cd0' };  // 未収金(カード)
    case NACC.shain:    return { icon: '割', tone: '#c98a24' };  // 社員割引
    case NACC.koukoku:  return { icon: '販', tone: '#c98a24' };  // 値引(広告宣伝費)
    case NACC.souiki:   return { icon: '差', tone: '#8a837c' };  // 相違金(両替差)
    default:            return { icon: '・', tone: '#6b6560' };
  }
}

const JournalCard: React.FC<{
  journal: NippouJournal;
  busy: boolean;
  partnersMap: Map<number, string> | null;
  itemsMap: Map<number, string> | null;
  tagsMap: Map<number, string> | null;
  onApprove: (j: NippouJournal) => void;
  onReject: (j: NippouJournal) => void;
  onKakeuri: (j: NippouJournal, line: KakeuriInstance, directive: KakeuriDirectiveInput) => void;
  onClassify: (j: NippouJournal, sig: UnknownSignal, category: string, partnerId: number | null) => void;
  onReeval: (j: NippouJournal) => void;
}> = ({ journal, busy, partnersMap, itemsMap, tagsMap, onApprove, onReject, onKakeuri, onClassify, onReeval }) => {
  const meta = STATUS_META[journal.status] || { label: journal.status, color: '#a89c92' };
  const deals: NippouDeal[] = Array.isArray(journal.deals_json) ? journal.deals_json : [];
  const [showLedger, setShowLedger] = useState(false);

  const debitRows: FlatRow[] = [];
  const creditRows: FlatRow[] = [];
  let debitSum = 0;
  let creditSum = 0;
  for (const deal of deals) {
    const details = Array.isArray(deal.details) ? deal.details : [];
    for (const d of details) {
      const amt = Number(d.amount) || 0;
      const side: 'debit' | 'credit' = d.entry_side || (deal.type === 'income' ? 'credit' : 'debit');
      const rowBase = {
        dealType: deal.type, detail: d,
        dealPartnerName: deal.partnerName, dealPartnerId: deal.partner_id,
      } as const;
      if (side === 'debit') { debitRows.push({ side: 'debit', ...rowBase }); debitSum += amt; }
      else { creditRows.push({ side: 'credit', ...rowBase }); creditSum += amt; }
    }
  }
  // 現金(売上) バランス行
  const cashLine = creditSum - debitSum;
  if (cashLine > 0) { debitRows.push({ side: 'debit', synthetic: { name: '現金(売上)', amount: cashLine } }); debitSum += cashLine; }
  else if (cashLine < 0) { creditRows.push({ side: 'credit', synthetic: { name: '現金(売上)', amount: -cashLine } }); creditSum += -cashLine; }

  const debitTotal = debitSum;
  const creditTotal = creditSum;
  const balanced = debitTotal === creditTotal;
  const discrepancy = debitTotal - creditTotal;
  const maxRows = Math.max(debitRows.length, creditRows.length, 1);
  const warnings = extractWarnings(journal.audit_json);
  const dow = dayOfWeek(journal.business_date);

  const factors = heldFactors(journal);
  const kakeuris = factors.includes('held_kakeuri') ? kakeuriInstances(journal) : [];
  const unknowns = factors.includes('held_unknown') ? unknownSignals(journal) : [];
  const isBlocked = journal.status === 'blocked_no_bank' || factors.includes('blocked_no_bank');
  const isImbalance = journal.status === 'held_imbalance' || factors.includes('held_imbalance');
  const isBankUnmatched = journal.status === 'held_bank_unmatched' || factors.includes('held_bank_unmatched');
  const audit: any = journal.audit_json || {};

  const renderCell = (row: FlatRow | undefined) => {
    if (!row) return { name: '', amount: null as number | null, sub: '' };
    if (row.synthetic) return { name: row.synthetic.name, amount: row.synthetic.amount, sub: '' };
    const d = row.detail!;
    const resolvePartnerName = (): string | null => {
      if (d.partnerName) return d.partnerName;
      if (d.partner_id != null && partnersMap?.has(Number(d.partner_id))) return partnersMap.get(Number(d.partner_id)) || null;
      if (row.dealPartnerName) return row.dealPartnerName;
      if (row.dealPartnerId != null && partnersMap?.has(Number(row.dealPartnerId))) return partnersMap.get(Number(row.dealPartnerId)) || null;
      return null;
    };
    const partnerName = resolvePartnerName();
    const subParts: string[] = [];
    if (d.itemName) subParts.push(d.itemName);
    if (partnerName) subParts.push(partnerName);
    if ((d as any).card_brand_unknown) subParts.push('⚠ブランド不明(入金時に確定)');
    if (d.sectionName) subParts.push(d.sectionName);
    if (d.taxCodeName) subParts.push(d.taxCodeName);
    return {
      name: d.accountName || (d.account_item_id != null ? `科目 ${d.account_item_id}` : '—'),
      amount: Number(d.amount) || 0,
      sub: subParts.join(' · '),
    };
  };

  // 'error' = 部分登録失敗行。 worker が freee_deal_ids を deal 単位で永続化しており、
  //   再承認は登録済 deal をスキップして続きから resume する (2026-07-13 fix) → 再試行可能に。
  const registerable = journal.status === 'proposed' || journal.status === 'suggested' || journal.status === 'approved' || journal.status === 'error';

  return (
    <div style={S.card(journal.status)}>
      {/* ヘッダー */}
      <div style={S.cardHeader}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <MdStore size={18} color="#8a837c" />
            <span style={S.storeTitle}>{journal.store_name || `部門 ${journal.section_id ?? '?'}`}</span>
            <span style={S.badge(meta.color)}>{meta.label}</span>
          </div>
          <div style={S.dateSub}>
            <MdEventNote size={13} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
            {journal.business_date}{dow ? ` (${dow})` : ''}
            {journal.section_id != null && <span style={{ marginLeft: 8, color: '#b0a89e' }}>sec {journal.section_id}</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' as const }}>
          {balanced
            ? <StatusIcon type="done">貸借一致 {yen(debitTotal)}</StatusIcon>
            : <StatusIcon type="error">貸借不一致 差額 {yen(discrepancy)}</StatusIcon>}
          {!balanced && (
            <div style={{ fontSize: '0.75rem', color: '#c94444', marginTop: 2 }}>
              借 {yen(debitTotal)} / 貸 {yen(creditTotal)}
            </div>
          )}
        </div>
      </div>

      {/* 複式仕訳 (借方=左 / 貸方=右) — freee タグ付き */}
      <div style={S.jPanel}>
        <div style={S.jGrid}>
          {([['debit', '借方', debitRows, debitTotal], ['credit', '貸方', creditRows, creditTotal]] as const).map(([side, label, rows, total]) => (
            <div key={side} style={side === 'debit' ? { borderRight: '1px solid #ece9e4' } : undefined}>
              <div style={S.jColHead(side)}>
                <span>{label}</span>
                <span style={S.jColHint}>{side === 'debit' ? 'DEBIT' : 'CREDIT'}</span>
              </div>
              {rows.map((row, i) => {
                const d: any = row.detail;
                const m2 = nippouLineMeta(d?.account_item_id, side);
                const name = row.synthetic ? row.synthetic.name : (d?.accountName || `科目 ${d?.account_item_id ?? '—'}`);
                const amount = row.synthetic ? row.synthetic.amount : (Number(d?.amount) || 0);
                const partnerName = !row.synthetic
                  ? (d?.partnerName || (d?.partner_id != null ? partnersMap?.get(Number(d.partner_id)) : null) || row.dealPartnerName || null)
                  : null;
                const vat = Number(d?.vat) || 0;
                return (
                  <div key={i} style={S.jEntry}>
                    <div style={S.jEntryTop}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                        <span style={{ ...S.jAccChip, background: m2.tone }}>{m2.icon}</span>
                        <span style={S.jAcc}>{name}</span>
                      </span>
                      <span style={S.jAmt}>{yen(amount)}</span>
                    </div>
                    {!row.synthetic && (
                      <div style={S.jTags}>
                        {d?.taxCodeName && <span style={S.jTaxTag}>{d.taxCodeName}</span>}
                        {d?.sectionName && <span style={S.jTag}><span style={S.jTagK}>部門</span>{d.sectionName}</span>}
                        {d?.itemName && <span style={S.jTag}><span style={S.jTagK}>品目</span>{d.itemName}</span>}
                        {partnerName && <span style={S.jTag}><span style={S.jTagK}>取引先</span>{partnerName}</span>}
                        {vat > 0 && <span style={S.jVatTag}>内消費税 {yen(vat)}</span>}
                        {d?.card_brand_unknown && <span style={S.jWarnTag}>⚠ ブランド不明</span>}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={S.jColTotal}><span>{label}計</span><span>{yen(total)}</span></div>
            </div>
          ))}
        </div>
        <div style={S.jBalance(balanced)}>
          {balanced
            ? <><MdCheckCircle size={15} /> 貸借一致 {yen(debitTotal)}</>
            : <><MdWarning size={15} /> 貸借不一致 差額 {yen(discrepancy)}</>}
        </div>
        <div style={S.jFooter}>
          <span><span style={S.jTagK}>決済口座 </span>現金（売上）</span>
          <span><span style={S.jTagK}>メモタグ </span>日報</span>
        </div>
      </div>
      {/* ALSOK/銀行 入金突合の内訳 (どの投入がどの営業日に対応したか) */}
      {((audit.alsok_match && audit.alsok_match.note) || audit.bank_match_note) && (
        <div style={S.matchNote}>現金入金の突合: {(audit.alsok_match && audit.alsok_match.note) || audit.bank_match_note}</div>
      )}
      {audit.misattribution_suspect && (
        <div style={S.misattrNote}>⚠ 他店誤投入(入金カード取り違え): {audit.misattribution_suspect.note}</div>
      )}
      {/* freee送信形式の行明細 (補助・トグル) */}
      <button style={S.toggleLink} onClick={() => setShowLedger(!showLedger)}>
        {showLedger ? '▾ freee送信形式(行明細)を隠す' : '▸ freee送信形式(行明細)を表示'}
      </button>
      {showLedger && (
      <table style={S.table}>
        <colgroup>
          <col style={{ width: '32%' }} /><col style={{ width: '18%' }} />
          <col style={{ width: '32%' }} /><col style={{ width: '18%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={S.th}>借方科目</th>
            <th style={{ ...S.th, ...S.thAmt }}>借方金額</th>
            <th style={{ ...S.th, ...S.divider }}>貸方科目</th>
            <th style={{ ...S.th, ...S.thAmt }}>貸方金額</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: maxRows }).map((_, i) => {
            const left = renderCell(debitRows[i]);
            const right = renderCell(creditRows[i]);
            return (
              <tr key={i}>
                <td style={S.td}>
                  {left.name || <span style={{ color: '#d0c9c0' }}>—</span>}
                  {left.sub && <div style={S.caption}>{left.sub}</div>}
                </td>
                <td style={{ ...S.td, ...S.amt }}>{left.amount != null ? yen(left.amount) : ''}</td>
                <td style={{ ...S.td, ...S.divider }}>
                  {right.name || <span style={{ color: '#d0c9c0' }}>—</span>}
                  {right.sub && <div style={S.caption}>{right.sub}</div>}
                </td>
                <td style={{ ...S.td, ...S.amt }}>{right.amount != null ? yen(right.amount) : ''}</td>
              </tr>
            );
          })}
          <tr style={S.totalRow}>
            <td style={S.td}>借方計</td>
            <td style={{ ...S.td, ...S.amt }}>{yen(debitTotal)}</td>
            <td style={{ ...S.td, ...S.divider }}>
              貸方計{' '}
              {balanced
                ? <MdCheckCircle size={14} color="#2da663" style={{ verticalAlign: 'text-bottom' }} />
                : <span style={{ color: '#c94444', fontWeight: 700 }}>差額 {yen(discrepancy)}</span>}
            </td>
            <td style={{ ...S.td, ...S.amt }}>{yen(creditTotal)}</td>
          </tr>
        </tbody>
      </table>
      )}

      {/* deal 内訳 */}
      {deals.length > 0 && (
        <div style={{ ...S.caption, marginTop: '8px' }}>
          内訳: {deals.map((d, i) => (
            <span key={i} style={{ marginRight: 10 }}>
              {d.type === 'income' ? '収入' : '支出'}{d.partnerName ? ` / ${d.partnerName}` : ''} ({(d.details || []).length}行)
            </span>
          ))}
        </div>
      )}

      {/* 掛売 指示 (held_kakeuri) */}
      {kakeuris.length > 0 && (
        <div style={S.heldBox}>
          <div style={S.heldTitle}>
            <MdWarning size={16} /> 掛売を検出 — 取引先 / 勘定 / 方針を指示してください (自動学習されません)
          </div>
          {kakeuris.map((k, i) => (
            <KakeuriDirectiveRow
              key={`${k.line_ref}_${i}`}
              instance={k}
              partnersMap={partnersMap}
              itemsMap={itemsMap}
              tagsMap={tagsMap}
              busy={busy}
              onApply={(directive) => onKakeuri(journal, k, directive)}
            />
          ))}
        </div>
      )}

      {/* 未知分類 (held_unknown) */}
      {unknowns.length > 0 && (
        <div style={{ ...S.heldBox, background: '#fff7ec', borderColor: '#f3d39a' }}>
          <div style={{ ...S.heldTitle, color: '#8b5a14' }}>
            <MdWarning size={16} /> 未知の決済/値引/クーポンを検出 — 分類を確定してください
          </div>
          {unknowns.map((sig, i) => (
            <ClassifyRow
              key={`${sig.source_key}_${i}`}
              signal={sig}
              partnersMap={partnersMap}
              busy={busy}
              onApply={(category, partnerId) => onClassify(journal, sig, category, partnerId)}
            />
          ))}
          <Note>分類確定後、 当該日報を「生成 (force)」で再生成すると提案 (proposed) に昇格します。</Note>
        </div>
      )}

      {/* 入金未取得 (blocked_no_bank) */}
      {isBlocked && (
        <div style={S.heldBox}>
          <div style={S.heldTitle}>
            <MdAccountBalance size={16} /> 銀行入金が未取得のため登録保留 (blocked_no_bank)
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#6b6560' }}>
            <div>突合状態: <strong>{audit.bank_match || '—'}</strong></div>
            {audit.expected_deposit_date && <div>入金予定日: <strong>{audit.expected_deposit_date}</strong></div>}
            {audit.cash_total != null && <div>現金売上(入金予定額): <strong>{yen(audit.cash_total)}</strong></div>}
            {audit.bank_match_note && <div>備考: {audit.bank_match_note}</div>}
            {audit.misattribution_suspect && (
              <div style={{ marginTop: 4, color: '#b3742e', fontWeight: 600 }}>
                ⚠ 他店誤投入の疑い: {audit.misattribution_suspect.note}
              </div>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            <Button small disabled={busy} onClick={() => onReeval(journal)}>
              {busy ? <InlineSpinner isLoading /> : '銀行入金を再評価'}
            </Button>
          </div>
        </div>
      )}

      {/* 銀行入金 照合不可 (held_bank_unmatched) — 借=貸は成立、 両替疑いで銀行入金が照合不能 */}
      {isBankUnmatched && (
        <div style={S.heldBox}>
          <div style={S.heldTitle}>
            <MdAccountBalance size={16} /> 銀行入金が照合できません (両替疑い) — 借方=貸方は一致
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#6b6560' }}>
            <div>仕訳内部は整合(借=貸)していますが、計算上の現金入金額と実際の銀行入金が一致しません。</div>
            {audit.nyukinki != null && <div>計算上の入金額: <strong>{yen(audit.nyukinki)}</strong></div>}
            {audit.bank_deposit != null && <div>銀行入金(候補): <strong>{yen(audit.bank_deposit)}</strong></div>}
            {audit.bank_deposit_date && <div>入金日: <strong>{audit.bank_deposit_date}</strong></div>}
            {audit.bank_match_note && <div>備考: {audit.bank_match_note}</div>}
            {audit.misattribution_suspect && (
              <div style={{ marginTop: 4, color: '#b3742e', fontWeight: 600 }}>
                ⚠ 他店誤投入の疑い: {audit.misattribution_suspect.note}
              </div>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            <Button small disabled={busy} onClick={() => onReeval(journal)}>
              {busy ? <InlineSpinner isLoading /> : '銀行入金を再評価'}
            </Button>
          </div>
        </div>
      )}

      {/* 貸借不一致 (held_imbalance) */}
      {isImbalance && (
        <div style={S.heldBox}>
          <div style={S.heldTitle}>
            <MdWarning size={16} /> 貸借不一致 / 入金突合不能 — 調査が必要です
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#6b6560' }}>
            {audit.bank_match && <div>突合状態: <strong>{audit.bank_match}</strong></div>}
            {audit.expected_deposit_date && <div>入金予定日: <strong>{audit.expected_deposit_date}</strong></div>}
            {audit.cash_total != null && <div>現金売上: <strong>{yen(audit.cash_total)}</strong></div>}
            <div style={{ marginTop: 4 }}>自動登録できません。 スマレジ/銀行明細を確認のうえ手動対応してください。</div>
          </div>
          {(audit.bank_match === 'blocked_ambiguous') && (
            <div style={{ marginTop: 8 }}>
              <Button small disabled={busy} onClick={() => onReeval(journal)}>
                {busy ? <InlineSpinner isLoading /> : '銀行入金を再評価'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* audit 警告 */}
      {warnings.length > 0 && (
        <div style={S.warningBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 4 }}>
            <MdWarning size={15} /> 注意事項
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* 却下理由 */}
      {journal.status === 'rejected' && journal.reject_reason && (
        <div style={{ marginTop: '10px' }}><Note>却下理由: {journal.reject_reason}</Note></div>
      )}

      {/* 登録済 freee deal id */}
      {journal.status === 'registered' && Array.isArray(journal.freee_deal_ids) && journal.freee_deal_ids.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <Note>freee 登録済: {journal.freee_deal_ids.map((id) => `#${id}`).join(', ')}</Note>
        </div>
      )}

      {/* 部分登録失敗 */}
      {journal.status === 'error' && (
        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', background: '#fdecec', border: '1px solid #f0c4c4', color: '#a33', fontSize: '0.8125rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 4 }}>
            <MdWarning size={15} /> 一部のみ登録 — 手動確認が必要です
          </div>
          {Array.isArray(journal.freee_deal_ids) && journal.freee_deal_ids.length > 0 && (
            <div>登録済 freee 取引: {journal.freee_deal_ids.map((id) => `#${id}`).join(', ')}</div>
          )}
        </div>
      )}

      {/* 操作 */}
      {(registerable || journal.status === 'proposed') && (
        <div style={S.actions}>
          {registerable && (
            <Button primary small disabled={busy || !balanced} onClick={() => onApprove(journal)}>
              {busy ? <InlineSpinner isLoading /> : '承認・登録 (プレビュー)'}
            </Button>
          )}
          {journal.status !== 'rejected' && journal.status !== 'registered' && (
            <Button danger small disabled={busy} onClick={() => onReject(journal)}>却下</Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 掛売指示 行 (inline)
// ---------------------------------------------------------------------------
// "名前 (#id)" 形式の datalist 入力から id を解決 (partner / item 共用)
function resolveIdFromQuery(query: string, map: Map<number, string> | null): number | null {
  const q = query.trim();
  if (!q) return null;
  const m = q.match(/#(\d+)\)?\s*$/);
  if (m) return Number(m[1]);
  if (!map) return null;
  const exact = Array.from(map.entries()).find(([, name]) => name === q);
  return exact ? exact[0] : null;
}

// 掛売指示 プリセット (2026-07-13): チップ click は全フィールドの自動入力のみ。
//   送信は従来の [この行に指示を適用] ボタン (誤爆防止)。
//   品目は itemsMap の実在名から解決 (完全一致 → 一意な部分一致)。見つからなければ品目空+警告
//   (実在しない item_id のでっち上げ禁止・id 実在確認の最終結果は worker 応答 note で返る想定)。
const KAKEURI_ACCOUNT_KOUKOKU = 858355826; // 広告宣伝費 (nippouLineMeta NACC.koukoku と同一)
const KAKEURI_PRESETS: Array<{
  key: string; label: string; accountId: number; itemName: string; lineNote: string;
}> = [
  { key: 'influencer', label: 'インフルエンサー飲食代', accountId: KAKEURI_ACCOUNT_KOUKOKU, itemName: 'インターネット関連費', lineNote: 'インフルエンサー飲食代' },
  { key: 'card_error', label: 'クレカ通信エラー', accountId: 891995955, itemName: 'クレジットカード', lineNote: 'クレジットカード通信エラー対応' },
];
// 勘定 SelectBox 候補: 確定値2種 (売掛金/未収金) + プリセット用 広告宣伝費
const KAKEURI_ACCOUNT_OPTIONS: Array<{ value: string; name: string }> = [
  ...NIPPOU_KAKEURI_ACCOUNTS.map((a) => ({ value: String(a.value), name: a.name })),
  { value: String(KAKEURI_ACCOUNT_KOUKOKU), name: `広告宣伝費 (${KAKEURI_ACCOUNT_KOUKOKU})` },
];

// 品目名 → itemsMap の実在 id 解決 (完全一致優先、無ければ一意な部分一致のみ。曖昧/0件は null)
function findItemByName(itemsMap: Map<number, string> | null, target: string): { id: number; name: string } | null {
  if (!itemsMap || !target) return null;
  const entries = Array.from(itemsMap.entries());
  const exact = entries.find(([, name]) => name === target);
  if (exact) return { id: exact[0], name: exact[1] };
  const partial = entries.filter(([, name]) => name && name.includes(target));
  if (partial.length === 1) return { id: partial[0][0], name: partial[0][1] };
  return null;
}

const KakeuriDirectiveRow: React.FC<{
  instance: KakeuriInstance;
  partnersMap: Map<number, string> | null;
  itemsMap: Map<number, string> | null;
  tagsMap: Map<number, string> | null;
  busy: boolean;
  onApply: (directive: KakeuriDirectiveInput) => void;
}> = ({ instance, partnersMap, itemsMap, tagsMap, busy, onApply }) => {
  const [accountId, setAccountId] = useState<number>(NIPPOU_KAKEURI_ACCOUNTS[0].value);
  const [partnerQuery, setPartnerQuery] = useState<string>('');
  const [noPartner, setNoPartner] = useState<boolean>(false);
  const [itemQuery, setItemQuery] = useState<string>('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [note, setNote] = useState<string>('');
  const [lineNote, setLineNote] = useState<string>('');
  const [presetWarn, setPresetWarn] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // プリセット適用 = フィールド自動入力のみ (送信しない)
  const applyPreset = (p: (typeof KAKEURI_PRESETS)[number]) => {
    setAccountId(p.accountId);
    setNoPartner(true);
    setPartnerQuery('');
    setLineNote(p.lineNote);
    setActivePreset(p.key);
    const item = findItemByName(itemsMap, p.itemName);
    if (item) {
      setItemQuery(`${item.name} (#${item.id})`);
      setPresetWarn(null);
    } else {
      setItemQuery('');
      setPresetWarn(`品目「${p.itemName}」が freee 品目一覧に見つかりません — 品目は空のままセットしました (実在しない品目IDは送信しません)`);
    }
  };

  // partner 候補: 入力からの部分一致 (最大 30 件)。 datalist で表示。
  const partnerOptions = useMemo(() => {
    if (!partnersMap) return [] as Array<{ id: number; name: string }>;
    const q = partnerQuery.trim();
    const all = Array.from(partnersMap.entries()).map(([id, name]) => ({ id, name }));
    const filtered = q ? all.filter((p) => p.name && p.name.includes(q)) : all;
    return filtered.slice(0, 30);
  }, [partnersMap, partnerQuery]);

  // 品目候補 (同形式・最大 30 件)
  const itemOptions = useMemo(() => {
    if (!itemsMap) return [] as Array<{ id: number; name: string }>;
    const q = itemQuery.trim();
    const all = Array.from(itemsMap.entries()).map(([id, name]) => ({ id, name }));
    const filtered = q ? all.filter((p) => p.name && p.name.includes(q)) : all;
    return filtered.slice(0, 30);
  }, [itemsMap, itemQuery]);

  // メモタグ一覧 (freee 全タグ・id 昇順)。 checkbox chip で複数選択。
  const tagOptions = useMemo(() => {
    if (!tagsMap) return [] as Array<{ id: number; name: string }>;
    return Array.from(tagsMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.id - b.id);
  }, [tagsMap]);
  const toggleTag = (id: number) => {
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const keyBase = `${instance.line_ref || 'na'}_${instance.source_key || ''}`;
  const partnerListId = `kakeuri_partner_${keyBase}`;
  const itemListId = `kakeuri_item_${keyBase}`;
  return (
    <div style={{ padding: '8px 0', borderTop: '1px dashed #f0c4c4' }}>
      {/* プリセットチップ (パネル最上部) — click で全フィールド自動入力のみ。送信は下の[適用]ボタン */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const, marginBottom: 6 }}>
        <span style={{ fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700 }}>プリセット:</span>
        {KAKEURI_PRESETS.map((p) => {
          const on = activePreset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              disabled={busy}
              title="勘定・品目・取引先なし・備考を自動入力します (送信は[この行に指示を適用]ボタン)"
              onClick={() => applyPreset(p)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                border: on ? '1px solid #2e7cf6' : '1px solid #d6d3d0',
                background: on ? '#eaf2fe' : '#fff', color: on ? '#1d5fd0' : '#6b6560',
              }}
            >
              <MdAutoAwesome size={12} /> {p.label}
            </button>
          );
        })}
      </div>
      {presetWarn && (
        <div style={{ marginBottom: 6, padding: '4px 8px', background: '#fff4e2', border: '1px solid #f3d39a', borderRadius: 6, color: '#8b5a14', fontSize: '0.75rem', fontWeight: 600 }}>
          ⚠ {presetWarn}
        </div>
      )}
      <div style={{ fontSize: '0.75rem', color: '#6b6560' }}>
        対象行: <strong>{instance.line_ref || '(自動判定)'}</strong>
        {instance.source_key ? ` / キー: ${instance.source_key}` : ''}
        {instance.amount != null ? ` / 金額: ${yen(instance.amount)}` : ''}
      </div>
      <div style={S.inlineRow}>
        <div style={S.field}>
          <span style={S.fieldLabel}>勘定科目</span>
          <SelectBox
            value={String(accountId)}
            options={KAKEURI_ACCOUNT_OPTIONS}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAccountId(Number(e.target.value))}
          />
        </div>
        <div style={{ ...S.field, minWidth: 240 }}>
          <span style={S.fieldLabel}>{noPartner ? '取引先 (なしで送信)' : '取引先 (掛売は必須)'}</span>
          <input
            list={partnerListId}
            value={partnerQuery}
            onChange={(e) => setPartnerQuery(e.target.value)}
            placeholder={noPartner ? '取引先なしで送信します' : '取引先名で検索…'}
            disabled={noPartner}
            style={{ padding: '6px 8px', border: '1px solid #d6d3d0', borderRadius: 6, fontSize: '0.8125rem', minWidth: 240, ...(noPartner ? { background: '#f3f1ef', color: '#a89c92' } : {}) }}
          />
          <datalist id={partnerListId}>
            {partnerOptions.map((p) => <option key={p.id} value={`${p.name} (#${p.id})`} />)}
          </datalist>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: '0.75rem', color: '#6b6560', cursor: 'pointer' }}>
            <input type="checkbox" checked={noPartner} onChange={(e) => setNoPartner(e.target.checked)} />
            取引先なし <span style={{ color: '#a89c92' }}>(タグ管理系勘定・ブランド不明クレジット用)</span>
          </label>
        </div>
        <div style={{ ...S.field, minWidth: 220 }}>
          <span style={S.fieldLabel}>品目 (任意)</span>
          <input
            list={itemListId}
            value={itemQuery}
            onChange={(e) => setItemQuery(e.target.value)}
            placeholder="品目名で検索… (未指定可)"
            style={{ padding: '6px 8px', border: '1px solid #d6d3d0', borderRadius: 6, fontSize: '0.8125rem', minWidth: 220 }}
          />
          <datalist id={itemListId}>
            {itemOptions.map((p) => <option key={p.id} value={`${p.name} (#${p.id})`} />)}
          </datalist>
        </div>
      </div>
      {tagOptions.length > 0 && (
        <div style={{ ...S.field, marginTop: 8 }}>
          <span style={S.fieldLabel}>メモタグ (任意・複数可)</span>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6, maxHeight: 96, overflowY: 'auto' as const, padding: '2px 0' }}>
            {tagOptions.map((t) => {
              const on = tagIds.includes(t.id);
              return (
                <label
                  key={t.id}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    padding: '3px 8px', borderRadius: 12, fontSize: '0.75rem',
                    border: on ? '1px solid #2e7cf6' : '1px solid #d6d3d0',
                    background: on ? '#eaf2fe' : '#fff', color: on ? '#1d5fd0' : '#6b6560',
                  }}
                >
                  <input type="checkbox" checked={on} onChange={() => toggleTag(t.id)} style={{ margin: 0 }} />
                  {t.name}
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div style={{ ...S.field, marginTop: 8 }}>
        <span style={S.fieldLabel}>備考 (任意・freee 摘要に入ります)</span>
        <input
          value={lineNote}
          onChange={(e) => setLineNote(e.target.value)}
          placeholder="例: インフルエンサー飲食代 / クレジットカード通信エラー対応"
          style={{ padding: '6px 8px', border: '1px solid #d6d3d0', borderRadius: 6, fontSize: '0.8125rem', maxWidth: 440 }}
        />
      </div>
      <div style={{ ...S.field, marginTop: 8 }}>
        <span style={S.fieldLabel}>方針メモ (任意・監査用 — freee には入りません)</span>
        <TextArea value={note} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)} height={2} width="full" placeholder="例: 〇〇社への掛売・当月締め翌月入金" />
      </div>
      <div style={{ marginTop: 8 }}>
        <Button primary small disabled={busy} onClick={() => {
          const itemId = resolveIdFromQuery(itemQuery, itemsMap);
          if (itemQuery.trim() && itemId == null) {
            window.alert('品目を一覧から選択してください (品目名 (#id) 形式)。指定しない場合は空欄にしてください');
            return;
          }
          let pid: number | null = null;
          if (!noPartner) {
            pid = resolveIdFromQuery(partnerQuery, partnersMap);
            if (!pid) { window.alert('取引先を一覧から選択してください (取引先名 (#id) 形式)。取引先を付けない場合は「取引先なし」にチェック'); return; }
          }
          onApply({ accountId, partnerId: pid, noPartner, itemId, addTagIds: tagIds, note: note.trim(), lineNote: lineNote.trim() });
        }}>
          {busy ? <InlineSpinner isLoading /> : 'この行に指示を適用'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 未知分類 行 (inline)
// ---------------------------------------------------------------------------
const CLASSIFY_CATEGORIES = [
  { value: 'ad_expense', name: '広告宣伝費 (販促)' },
  { value: 'employee_discount', name: '社員割引 (福利厚生)' },
  { value: 'payment', name: '決済手段 (未収金/取引先)' },
];
const ClassifyRow: React.FC<{
  signal: UnknownSignal;
  partnersMap: Map<number, string> | null;
  busy: boolean;
  onApply: (category: string, partnerId: number | null) => void;
}> = ({ signal, partnersMap, busy, onApply }) => {
  const [category, setCategory] = useState<string>(CLASSIFY_CATEGORIES[0].value);
  const [partnerQuery, setPartnerQuery] = useState<string>('');

  const partnerOptions = useMemo(() => {
    if (!partnersMap) return [] as Array<{ id: number; name: string }>;
    const q = partnerQuery.trim();
    const all = Array.from(partnersMap.entries()).map(([id, name]) => ({ id, name }));
    return (q ? all.filter((p) => p.name && p.name.includes(q)) : all).slice(0, 30);
  }, [partnersMap, partnerQuery]);
  const resolvePartnerId = (): number | null => {
    const q = partnerQuery.trim();
    if (!q) return null;
    const m = q.match(/#(\d+)\)?\s*$/);
    if (m) return Number(m[1]);
    if (!partnersMap) return null;
    const exact = Array.from(partnersMap.entries()).find(([, name]) => name === q);
    return exact ? exact[0] : null;
  };
  const listId = `classify_partner_${signal.source_key}`;
  const needPartner = category === 'payment';
  return (
    <div style={{ padding: '8px 0', borderTop: '1px dashed #f3d39a' }}>
      <div style={{ fontSize: '0.75rem', color: '#6b6560' }}>
        種別: <strong>{signal.kind}</strong> / キー: <strong>{signal.source_key}</strong>
        {signal.source_name ? ` (${signal.source_name})` : ''}
        {signal.contract_id ? ` / 契約: ${signal.contract_id}` : ''}
      </div>
      <div style={S.inlineRow}>
        <div style={S.field}>
          <span style={S.fieldLabel}>分類</span>
          <SelectBox value={category} options={CLASSIFY_CATEGORIES} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value)} />
        </div>
        {needPartner && (
          <div style={{ ...S.field, minWidth: 240 }}>
            <span style={S.fieldLabel}>取引先 (決済時)</span>
            <input list={listId} value={partnerQuery} onChange={(e) => setPartnerQuery(e.target.value)} placeholder="取引先名で検索…"
              style={{ padding: '6px 8px', border: '1px solid #d6d3d0', borderRadius: 6, fontSize: '0.8125rem', minWidth: 240 }} />
            <datalist id={listId}>{partnerOptions.map((p) => <option key={p.id} value={`${p.name} (#${p.id})`} />)}</datalist>
          </div>
        )}
        <Button primary small disabled={busy} onClick={() => onApply(category, resolvePartnerId())}>
          {busy ? <InlineSpinner isLoading /> : '分類を確定'}
        </Button>
      </div>
    </div>
  );
};

// ===========================================================================
// 承認・登録 dry_run プレビューダイアログ
// ===========================================================================
const ApproveDialog: React.FC<{
  dryRun: NippouApproveDryRun;
  journal: NippouJournal;
  partnersMap: Map<number, string> | null;
  registering: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ dryRun, journal, partnersMap, registering, onCancel, onConfirm }) => {
  const s = dryRun.summary;
  const hasErrors = (dryRun.errors && dryRun.errors.length > 0) || !s.balanced;
  const pn = (id: number | null) => (id != null ? (partnersMap?.get(id) || `#${id}`) : '');

  return (
    <div style={S.modalOverlay} onClick={() => { if (!registering) onCancel(); }}>
      <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #edebea' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>freee 登録確認 — {journal.store_name || `部門 ${journal.section_id}`}</div>
          <Note>{journal.business_date} / 下記が freee /api/1/deals に送信される予定の内容です。</Note>
        </div>
        <div style={{ padding: '16px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={S.badge(s.balanced ? '#2da663' : '#c94444')}>
              {s.balanced ? `Σ一致: 借${yen(s.sum_debit)} = 貸${yen(s.sum_credit)}` : `Σ不一致: 借${yen(s.sum_debit)} / 貸${yen(s.sum_credit)}`}
            </span>
            <span style={S.badge('#6b6560')}>{s.deal_count} deal</span>
            {s.gross_tax_include != null && <span style={S.badge('#48859d')}>税込グロス {yen(s.gross_tax_include)}</span>}
          </div>
          {dryRun.errors && dryRun.errors.length > 0 && (
            <div style={{ marginBottom: '12px' }}><MessageBlock error>{dryRun.errors.join(' / ')}</MessageBlock></div>
          )}
          {s.per_deal.map((d) => (
            <div key={d.seq} style={{ marginBottom: 14, border: '1px solid #edebea', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '6px 12px', background: '#fafaf8', fontSize: '0.8125rem', fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
                <span>{d.type === 'income' ? '収入' : '支出'} #{d.seq} {d.partner_id ? `/ ${pn(d.partner_id)}` : ''}</span>
                <span style={{ color: '#8a837c', fontWeight: 400 }}>ref: {d.ref_number}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={S.th}>借/貸</th><th style={S.th}>勘定</th><th style={S.th}>部門</th>
                    <th style={S.th}>税</th><th style={{ ...S.th, ...S.thAmt }}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  {d.accounts.map((a, i) => (
                    <tr key={i}>
                      <td style={S.td}>{a.entry_side === 'debit' ? '借方' : '貸方'}</td>
                      <td style={S.td}>{a.account_item_id}{a.item_id ? ` / 品目${a.item_id}` : ''}</td>
                      <td style={S.td}>{a.section_id ?? '—'}</td>
                      <td style={S.td}>税{a.tax_code}</td>
                      <td style={{ ...S.td, ...S.amt }}>{yen(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.payments && d.payments.length > 0 && (
                <div style={{ padding: '4px 12px', fontSize: '0.6875rem', color: '#8a837c' }}>
                  payments: {d.payments.map((p) => yen(p.amount)).join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid #edebea', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Note>登録は freee への実書込です (session lease 必須・取消不可)。</Note>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onCancel} disabled={registering}>キャンセル</Button>
            <Button primary onClick={onConfirm} disabled={registering || hasErrors}>
              {registering ? <><InlineSpinner isLoading /> 登録中…</> : 'freeeに登録 ▸'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------
const MONTH_OPTIONS = buildMonthOptions();
const INITIAL_YEAR_MONTH = currentYearMonth();

export const NippouPage: React.FC = () => {
  const [yearMonth, setYearMonth] = useState<string>(INITIAL_YEAR_MONTH);
  const [dateFrom, setDateFrom] = useState<string>(monthStart(INITIAL_YEAR_MONTH));
  const [dateTo, setDateTo] = useState<string>(monthEnd(INITIAL_YEAR_MONTH));
  const [sectionFilter, setSectionFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [journals, setJournals] = useState<NippouJournal[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [genProgress, setGenProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<NippouJournal | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [partnersMap, setPartnersMap] = useState<Map<number, string> | null>(null);
  const [itemsMap, setItemsMap] = useState<Map<number, string> | null>(null);
  const [tagsMap, setTagsMap] = useState<Map<number, string> | null>(null);

  // approve dry_run dialog
  const [dryRun, setDryRun] = useState<NippouApproveDryRun | null>(null);
  const [dryRunJournal, setDryRunJournal] = useState<NippouJournal | null>(null);
  const [registering, setRegistering] = useState<boolean>(false);

  // bulk approve
  const [bulkDry, setBulkDry] = useState<NippouBulkDryRun | null>(null);
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  // 一括登録フルスクリーン進捗オーバーレイ (null = 非表示)
  const [bulkRun, setBulkRun] = useState<BulkRegisterProgress | null>(null);

  // 台帳パネル
  const [learningRows, setLearningRows] = useState<NippouLearningRow[]>([]);
  const [bankPendingRows, setBankPendingRows] = useState<NippouBankPendingRow[]>([]);
  const [ledgerLoaded, setLedgerLoaded] = useState<boolean>(false);
  const [ledgerOpen, setLedgerOpen] = useState<boolean>(false);

  useEffect(() => {
    let alive = true;
    fetchPartnersMap().then((m) => { if (alive) setPartnersMap(m); }).catch(() => {});
    // 掛売指示の品目/メモタグ選択用 (module-level cache 済・失敗しても指示 UI は動く)
    fetchItemsMap().then((m) => { if (alive) setItemsMap(m); }).catch(() => {});
    fetchTagsMap().then((m) => { if (alive) setTagsMap(m); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const pushToast = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { id, kind, text }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 6500);
  }, []);

  // 取得: 日付範囲のみサーバに渡し、店舗/ステータスは client 側で絞り込む。
  //   (絞り込み結果から店舗候補/ステータスバッジを作ると、 絞った途端に他の候補が消えて
  //    「全て/全店舗」に戻さないと別の絞り込みへ遷移できなくなる incident の恒久対策)
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNippouJournalsRange(dateFrom, dateTo);
      const items = (res.items || []).slice().sort((a, b) => {
        if (a.business_date !== b.business_date) return a.business_date < b.business_date ? -1 : 1;
        const sa = a.section_id ?? 0; const sb = b.section_id ?? 0;
        if (sa !== sb) return sa - sb;
        return a.id - b.id;
      });
      setJournals(items);
    } catch (e: any) {
      setError(e?.message || '取得に失敗しました');
      setJournals([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const loadLedger = useCallback(async () => {
    try {
      const [lr, bp] = await Promise.all([
        fetchNippouLearning('pending_review').catch(() => ({ items: [] } as any)),
        fetchNippouBankPending('awaiting_bank').catch(() => ({ items: [] } as any)),
      ]);
      setLearningRows(lr.items || []);
      setBankPendingRows(bp.items || []);
      setLedgerLoaded(true);
    } catch { setLedgerLoaded(true); }
  }, []);
  useEffect(() => { loadLedger(); }, [loadLedger]);

  // 対象月変更 → 月初/月末を date 範囲に同期
  const onMonthChange = (ym: string) => {
    setYearMonth(ym);
    setDateFrom(monthStart(ym));
    setDateTo(monthEnd(ym));
  };

  // 生成 (generate dry_run:false)
  // 店舗(section)ごとに範囲全体を逐次実行する。 月一括 (全店×~31日=~310 store-day) を 1 リクエストにすると
  // worker のリクエスト時間上限を超えて応答が返らず、 ボタンが「生成中」のまま固まるため。
  // 1 店舗×範囲なら数十秒で完了し、 遠隔店の銀行突合 index も範囲全体で構築できる (突合品質を保つ)。
  // 各店舗リクエストは AbortController で 240s タイムアウト → 1 店舗ハングしても全体は止まらない。
  const onGenerate = useCallback(async (force: boolean) => {
    if (!window.confirm(`${dateFrom} 〜 ${dateTo} の日報を生成します。\n掛売/未知/入金未取得は held/blocked として保留されます。${force ? '\n(force: 既存の held/proposed を上書き再生成)' : ''}\n(店舗ごとに順次処理します)\nよろしいですか?`)) return;
    setGenerating(true);
    setGenProgress('');
    // 店舗(section)単位で範囲全体を生成する。 1 店舗×1ヶ月 ≒ 数十秒で timeout せず、
    //   かつ遠隔店の銀行突合 index は範囲全体で構築されるため突合品質を保てる
    //   (1日ずつだと遠隔店の入金が複数日にまたがり突合できず held_bank_unmatched が増える)。
    const secs = sectionFilter ? [Number(sectionFilter)] : NIPPOU_ALL_SECTIONS;
    const total = secs.length;
    const acc: Record<string, number> = {
      proposed: 0, held_kakeuri: 0, held_unknown: 0, blocked_no_bank: 0, held_bank_unmatched: 0, held_imbalance: 0, already_registered: 0,
    };
    let failedSecs = 0;
    try {
      const sid = (await ensureSessionLocal().catch(() => null)) || null;
      for (let i = 0; i < secs.length; i++) {
        const sec = secs[i];
        setGenProgress(`${i + 1}/${total}店舗 (sec ${sec})`);
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 240000);
        try {
          const res = await generateNippouJournals({
            date_from: dateFrom, date_to: dateTo, sections: [sec],
            dry_run: false, detect_held: true, detect_blocked: true, force, session_id: sid,
          }, { signal: ctrl.signal });
          const c = (res.counts || {}) as Record<string, number>;
          for (const k of Object.keys(acc)) acc[k] += (c[k] || 0);
        } catch (e: any) {
          failedSecs += 1;
        } finally {
          clearTimeout(timer);
        }
        await load().catch(() => {}); // 店舗ごとに一覧へ反映
      }
      pushToast(failedSecs ? 'error' : 'success',
        `生成完了 (${total}店舗${failedSecs ? `・${failedSecs}店舗失敗(再生成可)` : ''}): 提案${acc.proposed} / 掛売${acc.held_kakeuri} / 未知${acc.held_unknown} / 入金待${acc.blocked_no_bank} / 照合不可${acc.held_bank_unmatched} / 不一致${acc.held_imbalance} / 既登録${acc.already_registered}`);
      await load();
      await loadLedger();
    } catch (e: any) {
      pushToast('error', `生成失敗: ${e?.message || e}`);
    } finally {
      setGenerating(false);
      setGenProgress('');
    }
  }, [dateFrom, dateTo, sectionFilter, pushToast, load, loadLedger]);

  // 承認 → dry_run プレビューを開く
  const openApprove = useCallback(async (j: NippouJournal) => {
    setBusyId(j.id);
    try {
      const dr = await approveNippouJournalDryRun(j.id);
      setDryRun(dr); setDryRunJournal(j);
    } catch (e: any) {
      pushToast('error', `検証失敗: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  }, [pushToast]);

  // dry_run プレビュー OK → dry_run:false で実 freee 登録 (R2: user click 起点)
  const confirmRegister = useCallback(async () => {
    const j = dryRunJournal;
    if (!j) return;
    const label = `${j.store_name || `部門 ${j.section_id}`} ${j.business_date}`;
    setRegistering(true);
    try {
      const sid = (await ensureSessionLocal().catch(() => null)) || null;
      const r = await approveNippouJournal(j.id, sid);
      if (r.already) {
        setJournals((prev) => prev.map((x) => (x.id === j.id ? { ...x, status: 'registered' } : x)));
        pushToast('notice', `${label} は既に登録済です`);
      } else {
        const dealIds = Array.isArray(r.freee_deal_ids) ? r.freee_deal_ids : [];
        setJournals((prev) => prev.map((x) => (x.id === j.id ? { ...x, status: r.status || 'registered', freee_deal_ids: dealIds } : x)));
        pushToast('success', `${label} を freee に登録しました (取引 ${r.deal_count ?? dealIds.length} 件)`);
      }
      setDryRun(null); setDryRunJournal(null);
      await load();
    } catch (e: any) {
      const body = e?.body || {};
      if (body.error === 'partial_register_failed') {
        const okIds: number[] = Array.isArray(body.registered_freee_deal_ids) ? body.registered_freee_deal_ids : [];
        setJournals((prev) => prev.map((x) => (x.id === j.id ? { ...x, status: 'error', freee_deal_ids: okIds } : x)));
        // 2026-07-13 fix: 登録済 deal は deal 単位で永続化済 → 再承認は続きから resume (二重登録なし)
        pushToast('error', `${label}: 一部のみ登録 (deal_seq ${body.failed_deal_seq} で失敗)。原因解消後にこのカードを再承認すると登録済分をスキップして続きから登録されます`);
        setDryRun(null); setDryRunJournal(null);
        await load();
      } else if (body.error === 'claim_conflict') {
        pushToast('notice', `${label}: 別の登録処理が進行中の可能性があります。数分後に再試行してください (二重登録は防止されています)`);
        setDryRun(null); setDryRunJournal(null);
        await load();
      } else {
        pushToast('error', `${label} の登録に失敗: ${e?.name === 'AbortError' ? 'timeout — 登録済 deal は永続化済。再承認で続きから再開できます' : (e?.message || e)}`);
      }
    } finally {
      setRegistering(false);
    }
  }, [dryRunJournal, pushToast, load]);

  // 却下
  const openReject = useCallback((j: NippouJournal) => { setRejectTarget(j); setRejectReason(''); }, []);
  const confirmReject = useCallback(async () => {
    const j = rejectTarget;
    if (!j) return;
    setBusyId(j.id);
    try {
      const sid = (await ensureSessionLocal().catch(() => null)) || null;
      await rejectNippouJournal(j.id, rejectReason.trim(), sid);
      setJournals((prev) => prev.map((x) => (x.id === j.id ? { ...x, status: 'rejected', reject_reason: rejectReason.trim() || null } : x)));
      pushToast('notice', `${j.store_name || `部門 ${j.section_id}`} ${j.business_date} を却下しました`);
      setRejectTarget(null); setRejectReason('');
    } catch (e: any) {
      pushToast('error', `却下に失敗: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  }, [rejectTarget, rejectReason, pushToast]);

  // 掛売指示 (kakeuri-directive) — no_partner / item_id / add_tag_ids / line_note 対応 (worker 側実装済)
  const onKakeuri = useCallback(async (j: NippouJournal, line: KakeuriInstance, d: KakeuriDirectiveInput) => {
    setBusyId(j.id);
    try {
      const r = await saveNippouKakeuriDirective({
        journal_id: j.id,
        line_ref: line.line_ref,
        account_item_id: d.accountId,
        partner_id: d.noPartner ? null : d.partnerId,
        ...(d.noPartner ? { no_partner: true } : {}),
        item_id: d.itemId,
        ...(d.addTagIds.length ? { add_tag_ids: d.addTagIds } : {}),
        policy_note: d.note,
        line_note: d.lineNote || null,
      });
      pushToast(r.status === 'proposed' ? 'success' : 'notice',
        `掛売指示を適用 (${r.patched_lines}行) → status: ${statusLabel(r.status)}${r.note ? ` / ${r.note}` : ''}`);
      await load();
    } catch (e: any) {
      pushToast('error', `掛売指示に失敗: ${e?.body?.error || e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  }, [pushToast, load]);

  // 未知分類 (learn-classification)
  const onClassify = useCallback(async (j: NippouJournal, sig: UnknownSignal, category: string, partnerId: number | null) => {
    setBusyId(j.id);
    try {
      await saveNippouClassification({
        kind: sig.kind, contract_id: sig.contract_id ?? null, source_key: sig.source_key, source_name: sig.source_name ?? null,
        category, partner_id: partnerId,
      });
      pushToast('success', `分類を確定しました (${sig.source_key} → ${category})。「生成 (force)」で再生成すると提案へ昇格します`);
      await loadLedger();
    } catch (e: any) {
      pushToast('error', `分類確定に失敗: ${e?.body?.error || e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  }, [pushToast, loadLedger]);

  // 銀行 再評価 (bank-reeval)
  const onReeval = useCallback(async (j: NippouJournal) => {
    setBusyId(j.id);
    try {
      const sid = (await ensureSessionLocal().catch(() => null)) || null;
      const r = await reevalNippouBank({ date_from: j.business_date, date_to: j.business_date, section: j.section_id ?? undefined, session_id: sid });
      const c = r.counts || ({} as any);
      pushToast(c.resolved ? 'success' : 'notice',
        `再評価: 解消${c.resolved || 0} / 保留継続${c.still_blocked || 0} / escalated${c.escalated || 0}`);
      await load();
      await loadLedger();
    } catch (e: any) {
      pushToast('error', `再評価に失敗: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  }, [pushToast, load, loadLedger]);

  // 一括承認プレビュー → 確定
  const onBulkPreview = useCallback(async () => {
    setBulkBusy(true);
    try {
      const sections = sectionFilter ? [Number(sectionFilter)] : undefined;
      const res = await bulkApproveNippouDryRun(dateFrom, dateTo, sections);
      setBulkDry(res);
    } catch (e: any) {
      pushToast('error', `一括プレビュー失敗: ${e?.message || e}`);
    } finally {
      setBulkBusy(false);
    }
  }, [dateFrom, dateTo, sectionFilter, pushToast]);

  // 店舗(section)ごとに分割して一括登録する。 全店×範囲を1リクエストにすると、 freee POST が多すぎて
  //   worker のリクエスト時間を超える + 処理中に freee アクセストークン(6h)が失効し残りが 401 になる
  //   (2026-06-15 incident)。 section ごとなら各リクエスト開始時にトークンを再取得(期限切れなら自動 refresh)。
  // 2026-07-13 280件実戦監査 fix:
  //   - server 側も 1 リクエスト上限 (journal ≤40 / 累積 deal ≤200) で丸め remaining_count を返すため、
  //     section 内でも remaining が尽きるまでループ (登録済は WHERE で消える = resume 安全)
  //   - フルスクリーン進捗オーバーレイ (グレーアウト + n/N + 失敗表示 + 再試行) — 請求書一括承認の流儀
  //   - チャンク間 800ms ウェイト (freee rate limit 配慮)
  //   - client timeout 240s (client.ts) — 1 チャンクのハングが全体を無期限停止させない
  const runBulkRegister = useCallback(async (expectedTotal: number) => {
    const secs = sectionFilter ? [Number(sectionFilter)] : NIPPOU_ALL_SECTIONS;
    setBulkDry(null);
    setBulkBusy(true);
    let registered = 0, failed = 0, errChunks = 0;
    const failures: string[] = [];
    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    const render = (storeLabel: string, phase: 'running' | 'error' = 'running', errorText?: string) =>
      setBulkRun({ phase, storeLabel, done: registered + failed, total: expectedTotal, registered, failed, failures: failures.slice(0, 20), errorText });
    render('準備中…');
    try {
      const sid = await ensureSessionLocal();
      if (!sid) {
        render('', 'error', 'session open に失敗しました。通信状態を確認して [再試行] してください。');
        return;
      }
      for (let i = 0; i < secs.length; i++) {
        const secLabel = `${i + 1}/${secs.length}店舗 (sec ${secs[i]})`;
        render(secLabel);
        // server 上限で丸められた残り (remaining_count) が尽きるまで同条件で再リクエスト。
        //   pass 上限 8 = 40 journal × 8 ≫ 1店舗×1ヶ月 (~31 journal) の安全弁 (無限ループ防止)。
        for (let pass = 0; pass < 8; pass++) {
          let res: NippouBulkResult;
          try {
            res = await bulkApproveNippou(dateFrom, dateTo, sid, [secs[i]]);
          } catch (e: any) {
            errChunks += 1;
            const isAbort = e?.name === 'AbortError';
            failures.push(`sec ${secs[i]}: ${isAbort ? 'timeout (240s) — 処理中断。登録済 deal は永続化済で、再実行時は続きから自動resume' : (e?.message || String(e))}`);
            break;
          }
          registered += res.registered_count || 0;
          failed += res.failed_count || 0;
          for (const r of res.results || []) {
            if (r && r.ok === false && !r.already) {
              failures.push(`${r.store_name || `sec ${r.section_id}`} ${r.business_date || ''}: ${r.error || '失敗'}${r.failed_deal_seq != null ? ` (deal_seq ${r.failed_deal_seq})` : ''}`);
            }
          }
          render(secLabel);
          if (!res.remaining_count || res.remaining_count <= 0) break;
          await wait(800); // チャンク間ウェイト (freee rate limit 配慮)
        }
        await load().catch(() => {});
        if (i < secs.length - 1) await wait(800);
      }
      if (failed === 0 && errChunks === 0) {
        pushToast('success', `一括登録完了: ${registered}件 登録`);
        setBulkRun(null);
      } else {
        render('', 'error',
          `${registered}件 登録 / 失敗 ${failed}件${errChunks ? ` / ${errChunks}店舗で中断` : ''}。` +
          '登録済 deal は journal ごとに永続化済のため、再試行しても二重登録されません。' +
          '失敗行 (status=エラー) は各カードの承認から個別に再試行 (続きから resume)、中断分は [再試行] で再実行できます (処理中の行は数分間スキップされることがあります)。');
      }
      await load();
    } finally {
      setBulkBusy(false);
    }
  }, [dateFrom, dateTo, sectionFilter, pushToast, load]);

  const onBulkConfirm = useCallback(async () => {
    await runBulkRegister(bulkDry?.eligible_count ?? 0);
  }, [bulkDry, runBulkRegister]);

  // 失敗/中断後の再試行 — 未登録分 (proposed + stale approved 遺骸) のみ再実行される (server WHERE + claim が保証)
  const onBulkRetry = useCallback(async () => {
    const remainingEstimate = bulkRun ? Math.max(0, bulkRun.total - bulkRun.registered) : 0;
    await runBulkRegister(remainingEstimate);
  }, [bulkRun, runBulkRegister]);

  // 店舗フィルタ option
  const storeOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const j of journals) {
      if (j.section_id != null) m.set(String(j.section_id), j.store_name || `部門 ${j.section_id}`);
    }
    return Array.from(m.entries()).sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([id, name]) => ({ value: id, name: `${name} (sec ${id})` }));
  }, [journals]);

  // 店舗で絞った集合 (ステータスバッジの件数はこの集合で数える)
  const sectionJournals = useMemo(
    () => (sectionFilter ? journals.filter((j) => String(j.section_id) === sectionFilter) : journals),
    [journals, sectionFilter],
  );
  // 表示対象 (店舗 + ステータス)
  const visibleJournals = useMemo(
    () => (statusFilter ? sectionJournals.filter((j) => j.status === statusFilter) : sectionJournals),
    [sectionJournals, statusFilter],
  );
  const byStatus = useMemo(() => {
    const m: Record<string, number> = {};
    for (const j of sectionJournals) m[j.status] = (m[j.status] || 0) + 1;
    return m;
  }, [sectionJournals]);

  return (
    <Container>
      <div style={{ padding: '16px 0' }}>
        <PageTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MdRestaurantMenu size={22} /> 日報処理 (自動生成システム)
          </span>
        </PageTitle>
        <div style={{ marginTop: 4 }}>
          <Note>
            スマレジ日報を複式仕訳に自動生成し、対応待ち(承認/登録可) を確認・承認して freee に登録します。
            掛売・未知決済は「対応待ち」、入金待ちは「処理中」(銀行入金の到着で自動解消・操作不要) です。
            実 freee 登録は承認クリック時のみ実行されます。{UNIFIED_LEGEND}。
          </Note>
        </div>

        {/* 生成バー */}
        <div style={{ ...S.controlBar, marginTop: 16 }}>
          <div style={S.field}>
            <span style={S.fieldLabel}>対象月</span>
            <SelectBox value={yearMonth} options={MONTH_OPTIONS.map((m) => ({ value: m, name: m }))}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onMonthChange(e.target.value)} />
          </div>
          <div style={S.field}>
            <span style={S.fieldLabel}>開始日</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={S.dateInput} />
          </div>
          <div style={S.field}>
            <span style={S.fieldLabel}>終了日</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={S.dateInput} />
          </div>
          <Button primary onClick={() => onGenerate(false)} disabled={generating || loading}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {generating ? <InlineSpinner isLoading /> : <MdAutoAwesome size={16} />} {generating && genProgress ? `生成中 ${genProgress}` : '生成'}
            </span>
          </Button>
          <Button onClick={() => onGenerate(true)} disabled={generating || loading}>生成 (force)</Button>
          <Button onClick={onBulkPreview} disabled={loading || bulkBusy || generating}>
            {bulkBusy ? <InlineSpinner isLoading /> : '一括承認・登録'}
          </Button>
        </div>

        {/* フィルタバー */}
        <div style={S.controlBar}>
          <div style={S.field}>
            <span style={S.fieldLabel}>店舗 (部門)</span>
            <SelectBox value={sectionFilter} options={[{ value: '', name: '全店舗' }, ...storeOptions]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSectionFilter(e.target.value)} />
          </div>
          <div style={S.field}>
            <span style={S.fieldLabel}>ステータス</span>
            <SelectBox value={statusFilter}
              options={[{ value: '', name: '全て' }, ...FILTER_STATUSES.map((s) => ({ value: s, name: statusLabel(s) }))]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)} />
          </div>
          <Button onClick={() => load()} disabled={loading}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdRefresh size={16} /> 再取得
            </span>
          </Button>
        </div>

        {/* KPI status バッジ (クリックでフィルタ) */}
        {journals.length > 0 && (
          <div style={S.kpiRow}>
            <span style={S.kpiChip(statusFilter === '', '#6b6560')} onClick={() => setStatusFilter('')}>
              全て {sectionJournals.length}
            </span>
            {FILTER_STATUSES.filter((s) => byStatus[s]).map((s) => (
              <span key={s} style={S.kpiChip(statusFilter === s, statusColor(s))} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}>
                {statusLabel(s)} {byStatus[s]}
              </span>
            ))}
          </div>
        )}

        {/* 台帳パネル (Accordion) */}
        <div style={{ marginBottom: 16 }}>
          <AccordionPanel
            border="both"
            open={ledgerOpen}
            onClick={() => setLedgerOpen((v) => !v)}
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                <MdSchool size={16} /> 台帳: 学習 pending ({learningRows.length}) / 入金待ち ({bankPendingRows.length})
              </span>
            }
          >
            <div style={{ padding: '8px 4px' }}>
                    {!ledgerLoaded && <Note>読み込み中…</Note>}
                    {ledgerLoaded && (
                      <>
                        <div style={{ fontWeight: 700, fontSize: '0.8125rem', margin: '4px 0' }}>未確定の分類学習 (pending_review)</div>
                        {learningRows.length === 0 ? <Note>未確定の学習はありません。</Note> : (
                          <table style={S.table}>
                            <thead><tr>
                              <th style={S.th}>種別</th><th style={S.th}>キー</th><th style={S.th}>名称</th><th style={S.th}>契約</th>
                            </tr></thead>
                            <tbody>
                              {learningRows.map((r) => (
                                <tr key={r.id}>
                                  <td style={S.td}>{r.kind}</td><td style={S.td}>{r.source_key}</td>
                                  <td style={S.td}>{r.source_name || '—'}</td><td style={S.td}>{r.contract_id || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <div style={{ fontWeight: 700, fontSize: '0.8125rem', margin: '12px 0 4px' }}>入金未取得 台帳 (awaiting_bank)</div>
                        {bankPendingRows.length === 0 ? <Note>入金待ちの台帳はありません。</Note> : (
                          <table style={S.table}>
                            <thead><tr>
                              <th style={S.th}>店舗</th><th style={S.th}>営業日</th><th style={S.th}>入金予定日</th>
                              <th style={{ ...S.th, ...S.thAmt }}>現金売上</th><th style={S.th}>理由</th>
                            </tr></thead>
                            <tbody>
                              {bankPendingRows.map((r) => (
                                <tr key={r.id}>
                                  <td style={S.td}>{r.store_name || `sec ${r.section_id}`}</td>
                                  <td style={S.td}>{r.business_date}</td>
                                  <td style={S.td}>{r.expected_deposit_date || '—'}</td>
                                  <td style={{ ...S.td, ...S.amt }}>{yen(r.cash_total)}</td>
                                  <td style={S.td}>{r.reason || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </>
                    )}
            </div>
          </AccordionPanel>
        </div>

        {/* 状態 */}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '24px 0', color: '#8a837c' }}>
            <InlineSpinner isLoading /> 読み込み中...
          </div>
        )}
        {error && !loading && (
          <div style={{ marginTop: 16 }}><MessageBlock error>取得エラー: {error}</MessageBlock></div>
        )}
        {!loading && !error && journals.length === 0 && (
          <div style={{ marginTop: 16 }}>
            <Note>{dateFrom} 〜 {dateTo} の日報データは該当ありません。「生成」を押すと対象範囲を生成します。</Note>
          </div>
        )}
        {!loading && !error && journals.length > 0 && visibleJournals.length === 0 && (
          <div style={{ marginTop: 16 }}>
            <Note>この絞り込み条件 (店舗/ステータス) に該当する日報はありません。</Note>
          </div>
        )}

        {/* 一覧 */}
        {!loading && !error && visibleJournals.map((j) => (
          <JournalCard
            key={j.id}
            journal={j}
            busy={busyId === j.id}
            partnersMap={partnersMap}
            itemsMap={itemsMap}
            tagsMap={tagsMap}
            onApprove={openApprove}
            onReject={openReject}
            onKakeuri={onKakeuri}
            onClassify={onClassify}
            onReeval={onReeval}
          />
        ))}
      </div>

      {/* 却下理由ダイアログ */}
      <TaskDialog
        title="日報を却下"
        isOpen={!!rejectTarget}
        primaryButtonLabel={rejectTarget && busyId === rejectTarget.id ? '却下中...' : '却下する'}
        closeButtonLabel="キャンセル"
        danger
        disabled={!!rejectTarget && busyId === rejectTarget.id}
        onPrimaryAction={confirmReject}
        onRequestClose={() => { setRejectTarget(null); setRejectReason(''); }}
        shouldCloseOnOverlayClickOrEsc={!(rejectTarget && busyId === rejectTarget.id)}
      >
        {rejectTarget && (
          <div style={{ minWidth: 320 }}>
            <div style={{ marginBottom: 8 }}>
              <Note>{rejectTarget.store_name || `部門 ${rejectTarget.section_id}`} / {rejectTarget.business_date}</Note>
            </div>
            <div style={{ ...S.fieldLabel, marginBottom: 4 }}>却下理由 (任意)</div>
            <TextArea value={rejectReason} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
              placeholder="例: スマレジ突合差異、金額誤り 等" height={4} width="full" />
          </div>
        )}
      </TaskDialog>

      {/* 承認 dry_run プレビューダイアログ */}
      {dryRun && dryRunJournal && (
        <ApproveDialog
          dryRun={dryRun}
          journal={dryRunJournal}
          partnersMap={partnersMap}
          registering={registering}
          onCancel={() => { if (!registering) { setDryRun(null); setDryRunJournal(null); } }}
          onConfirm={confirmRegister}
        />
      )}

      {/* 一括承認・登録 確認ダイアログ */}
      {bulkDry && (
        <div style={S.modalOverlay} onClick={() => { if (!bulkBusy) setBulkDry(null); }}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid #edebea' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>一括承認・登録 — {bulkDry.date_from} 〜 {bulkDry.date_to}</div>
              <Note>提案済 (proposed・貸借一致) の日報をまとめて freee に登録します。</Note>
            </div>
            <div style={{ padding: '16px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={S.badge('#2da663')}>登録対象 {bulkDry.eligible_count}件</span>
                {Object.keys(bulkDry.not_eligible || {}).length > 0 && (
                  <span style={S.badge('#d98324')}>
                    対象外 {Object.entries(bulkDry.not_eligible).map(([s, n]) => `${statusLabel(s)}${n}`).join(' / ')}
                  </span>
                )}
              </div>
              {bulkDry.eligible_count === 0 ? (
                <MessageBlock>登録対象 (提案済・貸借一致) の日報がありません。</MessageBlock>
              ) : (
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>店舗</th><th style={S.th}>営業日</th>
                    <th style={{ ...S.th, ...S.thAmt }}>借方計</th><th style={{ ...S.th, ...S.thAmt }}>貸方計</th>
                  </tr></thead>
                  <tbody>
                    {bulkDry.items.map((it) => (
                      <tr key={it.id}>
                        <td style={S.td}>{it.store_name || `sec ${it.section_id}`}</td>
                        <td style={S.td}>{it.business_date}</td>
                        <td style={{ ...S.td, ...S.amt }}>{yen(it.debit_total)}</td>
                        <td style={{ ...S.td, ...S.amt }}>{yen(it.credit_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #edebea', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Note>登録は freee への実書込です (session lease 必須)。</Note>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={() => setBulkDry(null)} disabled={bulkBusy}>キャンセル</Button>
                <Button primary onClick={onBulkConfirm} disabled={bulkBusy || bulkDry.eligible_count === 0}>
                  {bulkBusy ? <><InlineSpinner isLoading /> 一括登録中…</> : `一括でfreeeに登録 ▸ (${bulkDry.eligible_count}件)`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 一括登録フルスクリーン進捗オーバーレイ — 実行中はグレーアウト(操作不可)。 請求書一括承認の流儀 */}
      {bulkRun && (
        <div
          role="dialog"
          aria-modal="true"
          aria-busy={bulkRun.phase === 'running'}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(29,42,58,0.62)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', width: 560, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,.35)' }}>
            {bulkRun.phase === 'running' ? (
              <>
                <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>日報を freee に一括登録中</h2>
                <div style={{ fontSize: 12, color: '#6b6560', marginBottom: 16 }}>
                  処理が完了するまでこのままお待ちください — 店舗ごとに順次登録し、進捗は journal 単位で D1 に永続化されます (途中で止まっても再実行で続きから安全に再開できます)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <InlineSpinner isLoading />
                  <span style={{ fontFamily: '"SFMono-Regular",Consolas,Menlo,monospace', fontSize: 22, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                    登録中 {bulkRun.done}/{bulkRun.total}
                  </span>
                  <span style={{ fontSize: 12, color: '#6b6560' }}>{bulkRun.storeLabel}</span>
                </div>
                <ProgressBar value={bulkRun.done} maxValue={Math.max(bulkRun.total, 1)} width="full" ariaLabel="一括登録進捗" />
                <div style={{ marginTop: 12, fontSize: 12, color: '#6b6560' }}>
                  登録 {bulkRun.registered}件{bulkRun.failed > 0 ? ` / 失敗 ${bulkRun.failed}件` : ''} — freee への実書込です (取消不可)
                </div>
                {bulkRun.failures.length > 0 && (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: '#fdecea', border: '1px solid #e6b0ac', color: '#b3261e', fontSize: 12, fontWeight: 700, maxHeight: 120, overflowY: 'auto' }}>
                    {bulkRun.failures.map((f, i) => <div key={i}>{f}</div>)}
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 style={{ margin: '0 0 12px', fontSize: 18, color: '#b3261e', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdWarning size={22} /> 一括登録で失敗・中断があります
                </h2>
                <div style={{ padding: '12px 14px', borderRadius: 6, background: '#fdecea', border: '1px solid #e6b0ac', color: '#b3261e', fontSize: 13, fontWeight: 700, lineHeight: 1.6, marginBottom: 12, wordBreak: 'break-word' }}>
                  {bulkRun.errorText}
                </div>
                {bulkRun.failures.length > 0 && (
                  <div style={{ fontSize: 12, color: '#6b6560', lineHeight: 1.7, marginBottom: 16, maxHeight: 160, overflowY: 'auto', border: '1px solid #ece9e4', borderRadius: 6, padding: '8px 12px' }}>
                    {bulkRun.failures.map((f, i) => <div key={i}>{f}</div>)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button appearance="secondary" small onClick={() => setBulkRun(null)}>閉じる</Button>
                  <Button appearance="primary" small onClick={onBulkRetry} disabled={bulkBusy}>再試行 (未登録分のみ)</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* トースト */}
      {toasts.map((t) => (
        <FloatingMessageBlock key={t.id}
          success={t.kind === 'success'} error={t.kind === 'error'} notice={t.kind === 'notice'} info={t.kind === 'info'}
          onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}>
          {t.text}
        </FloatingMessageBlock>
      ))}
    </Container>
  );
};

export default NippouPage;
